<template>
    <div class="calendar">
        <div class="toolbar">
            <div class="toolbar__years">
                <a v-for="(year, idx) in [new Date(value.getFullYear() - 1, 0, 1), value, new Date(value.getFullYear() + 1, 0, 1)]" class="button" @click.prevent="selectYear(year.getFullYear())" :class="{'is-active': year == value}">
                    {{year.getFullYear()}}
                </a>
            </div>
            <div class="toolbar__months">
                <a class="button" @click.prevent="selectMonth(-1)">&lt;</a>
                <a class="button" :class="{'is-active': new Date().getFullYear() == value.getFullYear() && new Date().getMonth() == value.getMonth()}" @click.prevent="selectDate(new Date())">Heute</a>
                <a class="button" @click.prevent="selectMonth(1)">&gt;</a>
            </div>
        </div>
        <div class="columns is-multiline calendar-columns">
            <div v-for="(month, idx) in allMonths" class="column month-column">
                <div class="month-wrapper box">
                    <div class="head">{{moment(month[1][0].date).format('MMMM YYYY')}}</div>
                    <div class="month">
                        <div class="week-head">
                            <div v-for="(day, idx) in month[0]" class="day">
                                <span>{{ moment(day.date).format('dd') }}</span>
                            </div>
                        </div>
                        <div v-for="(week, idx) in month" class="week">
                            <div v-for="(day, idx2) in week" class="day" :class="getDayClasses(day)" :style="getDayStyles(day)">
                                <div class="tooltip" v-if="day && day.season" v-html="getTooltipContent(day.season, day)"></div>
                                <span v-if="day">{{ day.date.getDate() }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment';

    import { Tracking } from '../../../utils';

    export default {
        name: 'calendar',

        props: {
            options: {
                type: Object,
                'default': () => { return {}; },
            },
            seasons: {
                type: Array,
                'default': () => { return []; },
            },
            value: {
                type: Date,
                'default': () => { return new Date(); },
            },
        },

        data() {
            return {
                defaultOptions: {
                    numMonths: 3,
                    firstDayOfWeek: 1, // So = 0, Mo = 1, Di = 2, ...
                },
                allMonths: [],
                moment: moment,
                tooltip: {
                    content: '',
                    left: 0,
                    top: 0,
                },
            };
        },

        watch: {
            value() {
                this.renderCalendar();
            },
            seasons() {
                this.renderCalendar();
            }
        },

        computed: {
            computedOptions() {
                return Object.assign({}, this.defaultOptions, this.options);
            },

            displayMonths() {
                const numMonths = this.computedOptions.numMonths;
                const currentMonth = new Date(this.value.getFullYear(), this.value.getMonth(), 1);
                let left, right;
                if(numMonths % 2 === 0) {
                    left = numMonths / 2 - 1;
                    right = numMonths / 2;
                } else {
                    left = right = Math.floor(numMonths / 2);
                }

                let months = [];
                while(left > 0) {
                    let y = currentMonth.getFullYear();
                    let m = currentMonth.getMonth() - left;
                    if(m < 0) {
                        m = 11;
                        y--;
                    }
                    months.push(new Date(y, m, 1));
                    left--;
                }
                months.push(currentMonth);
                let idx = 1;
                while(right > 0) {
                    let y = currentMonth.getFullYear();
                    let m = currentMonth.getMonth() + idx;
                    if(m > 11) {
                        m = 0;
                        y++;
                    }
                    months.push(new Date(y, m, 1));
                    right--;
                    idx++;
                }

                return months;
            }
        },

        created() {
            this.renderCalendar();
        },

        methods: {
            getDaysForMonth(date) {
                const y = date.getFullYear();
                const m = date.getMonth();
                const numDays = new Date(y, m + 1, 0).getDate();
                let days = [];
                let day, season;
                for(let i = 0; i < numDays; i++) {
                    day = new Date(y, m, i + 1);
                    season = this.getSeasonForDay({date: day});
                    days.push({
                        type: 'normal',
                        date: day,
                        season: season,
                    });
                }

                let skipLength = days[0].date.getDay();
                let preLength = days.length + skipLength;

                let postLength = () => {
                    if(preLength % 7 === 0) {
                        return 0;
                    } else {
                        if(preLength < 35) {
                            return 35 - preLength;
                        } else {
                            return 42 - preLength;
                        }
                    }
                };

                // Add blank days to fill in before first day
                for(let i = 0; i < skipLength; i++) {
                    day = new Date(y, m, -i);
                    season = this.getSeasonForDay({date: day});
                    days.splice(0, 0, {type: 'pre', date: day, season: season});
                }

                // Add in the dummy filler days to make an even block
                for(let j = 0; j < postLength(); j++) {
                    day = new Date(y, m + 1, j + 1);
                    season = this.getSeasonForDay({date: day});
                    days.push({type: 'post', date: day, season: season});
                }

                return days;
            },

            getDayClasses(day) {
                if(!day) {
                    return {};
                }

                let obj = {
                    'is-today': moment(day.date).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')
                };
                obj[day.type] = true;

                return obj;
            },

            getDayStyles(day) {
                if(!day) {
                    return {};
                }

                let obj = {};
                const season = this.getSeasonForDay(day);
                if(season) {
                    obj['border-color'] = season.color;
                }

                return obj;
            },

            getSeasonForDay(day) {
                if(!day) {
                    return null;
                }
                const mom = moment(day.date);
                let foundSeason;
                for(let season of this.seasons) {
                    for(let timePeriod of season.time_periods) {
                        if(mom.isBetween(timePeriod.date_start, timePeriod.date_end, 'day', '[]')) {
                            if(season.is_lock) {
                                return season;
                            } else {
                                foundSeason = season;
                            }
                        }
                    }
                }

                return foundSeason;
            },

            getSeasonById(seasonId) {
                for(let season of this.seasons) {
                    if(season.id === seasonId) {
                        return season;
                    }
                }
                return null;
            },

            selectYear(v) {
                this.$emit('input', new Date(v, this.value.getMonth(), 1));

                Tracking.trackEvent('Seasons', 'calendar', 'selectYear', null);
            },

            selectDate(date) {
                this.$emit('input', date);

                Tracking.trackEvent('Seasons', 'calendar', 'selectDate', null);
            },

            selectMonth(add) {
                this.$emit('input', new Date(this.value.getFullYear(), this.value.getMonth() + add, this.value.getDate()));

                Tracking.trackEvent('Seasons', 'calendar', 'selectMonth', null);
            },

            getTooltipContent(season, day) {
                let timePeroids = [];
                const df = this.$t('generic.calendar.dateformatMomentjs');
                for(let timePeriod of season.time_periods) {
                    if(!this.timePeriodIsInPast(timePeriod) || this.timePeriodContainsDay(timePeriod, day)) {
                        timePeroids.push(`<strong>${moment(timePeriod.date_start).format(df)}</strong> ${this.$t('seasons.until')} <strong>${moment(timePeriod.date_end).format(df)}</strong>`);
                    }
                }
                return `<h4>${season.name}</h4><ul><li>${timePeroids.join('</li><li>')}</li></ul><span class="triangle"></span>`;
            },

            renderCalendar() {
                this.allMonths = [];
                let monthIdx = 0;
                for(let month of this.displayMonths) {
                    this.allMonths[monthIdx] = [];

                    let idx = 0;
                    let days = this.getDaysForMonth(month);
                    for(let weekDay = 0; weekDay < 7; weekDay++) {
                        this.allMonths[monthIdx][weekDay] = [];
                        for(let week = 0; week < 7; week++) {
                            this.allMonths[monthIdx][weekDay].push(days[idx++]);
                        }
                    }
                    monthIdx++;
                }
            },

            timePeriodIsInPast(period) {
                return moment().isAfter(moment(period.date_end));
            },

            timePeriodContainsDay(period, day) {
                const d = moment(day.date);
                return d.isSameOrAfter(moment(period.date_start)) && d.isSameOrBefore(moment(period.date_end));
            },
        },
    };
</script>

<style lang="scss">
    @import './calendar.scss';
</style>



// WEBPACK FOOTER //
// src/views/components/calendar/calendar.vue